<script setup>
import { computed, ref } from 'vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store.js';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();
const store = useStore();
const currentUserId = useMapGetter('getCurrentUserID');

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const isInternalChat = computed(() => !!props.stateInbox?.internal_chat);

const isInternalChatGroup = computed(
  () =>
    isInternalChat.value &&
    !!props.conversation.additional_attributes?.internal_chat_group
);

const internalChatParticipantName = computed(() => {
  if (!isInternalChat.value || isInternalChatGroup.value) return '';
  const { internal_initiator_id, internal_recipient_id } = props.conversation;
  const otherUserId =
    Number(internal_initiator_id) === Number(currentUserId.value)
      ? internal_recipient_id
      : internal_initiator_id;
  if (!otherUserId) return '';
  const agent = store.getters['agents/getAgentById'](otherUserId);
  return agent?.name || '';
});

const internalChatGroupName = computed(() => {
  if (!isInternalChatGroup.value) return '';
  const threads = store.getters['internalChat/getThreads'];
  const thread = threads.find(t => t.uuid === props.conversation.uuid);
  if (thread) {
    const explicitName = thread.group?.name?.trim();
    if (explicitName) return explicitName;
    const memberNames = (thread.group?.members || [])
      .map(m => m.name)
      .filter(Boolean)
      .join(', ');
    if (memberNames) return memberNames;
  }
  return props.conversation.additional_attributes?.group_name || '';
});

const currentContactName = computed(() => {
  if (isInternalChat.value) {
    if (isInternalChatGroup.value) {
      return internalChatGroupName.value || currentContact.value?.name || '';
    }
    if (internalChatParticipantName.value) {
      return internalChatParticipantName.value;
    }
  }
  return currentContact.value?.name;
});

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
